import { Injectable } from '@angular/core';

// Models
import { PaginatedData, ResponseData } from 'src/app/platform/models/utils';
import { ProductsData } from 'src/app/modules/products/models/products';
import { MetaData } from 'src/app/platform/models/utils';

// Services
import { UserService } from 'src/app/modules/account/user.service';
import { ApiService } from 'src/app/platform/services/api.service';
import { dateFormat, objectToString } from 'src/app/platform/utils/functions';
import { BehaviorSubject } from 'rxjs';

/**
 * This class handles all actions related to products and contracts
 */
@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  public contractsIsUpdate = new BehaviorSubject<boolean>(false);

  constructor(
    private userService: UserService,
    private apiService: ApiService
  ) { }

  // Contracts services

  /**
   * Execute the process of reloading the list of contracts
   */
  contractsUpdate(status: boolean): void {
    this.contractsIsUpdate.next(status);
  }

  /**
   * Get a list of contracts
   */
  contractsGet(page?: number | null, limit?: number | null, filters: any | null = null): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      let data: any = {page, limit};

      if (filters !== null) {
        data = {
          page,
          limit,
          tipos: filters.type,
          estados: filters.status,
          fechaEmisionDesde: dateFormat(filters.date.start, 'YYYY/MM/DD 00:00:00'),
          fechaEmisionHasta: dateFormat(filters.date.end, 'YYYY/MM/DD 23:59:59'),
          fechaVencimientoDesde: dateFormat(filters.dateExpiration.start, 'YYYY/MM/DD 00:00:00'),
          fechaVencimientoHasta: dateFormat(filters.dateExpiration.end, 'YYYY/MM/DD 23:59:59'),
        };
      }

      this.apiService.request('get', 'contratos/clienteId=' + this.userService.currentUser.value.client + objectToString(data), {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const productsData: Array<ProductsData> = [];

          success.items.forEach((value: any, index: number) => {
            productsData.push(new ProductsData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', new PaginatedData(productsData, success.totalRegistros, success.limiteRegistros, success.paginas, success.paginaActual)));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Incorporate a new contract
   */
  contractsAdd(plan: number): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('post', 'contratos/clienteId=' + this.userService.currentUser.value.client + '?planId=' + plan, {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          resolve(this.apiService.successDataPrepare(success.response.message, success));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Change the plan of a contract
   */
  contractsChange(contract: number, product: number): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('post', 'contratos/contratoId=' + contract + '?productoId=' + product, {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          resolve(this.apiService.successDataPrepare(success.response.message, success));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Request the cancellation of a contract
   */
  contractsDelete(contract: number, reason: number, comments: string, phone: string, email: string, schedule: string, contactByPhone: boolean, contactByEmail: boolean): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      const data = {
        motivoBajaId: reason,
        observaciones: comments,
        telefonoContacto: phone,
        mailContacto: email,
        horarioContacto: schedule,
        contactarTelefono: contactByPhone,
        contactarMail: contactByEmail
      }

      this.apiService.request('delete', 'contratos/contratoId=' + contract, data, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          resolve(this.apiService.successDataPrepare(success.response.message, success));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  // Products services

  /**
   * Get a list of products
   */
  productsGet(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'productos/productos', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const metaData: Array<MetaData> = [];

          success.items.forEach((value: any, index: number) => {
            metaData.push(new MetaData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', metaData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Get a list of qualified products for a replacement
   */
  productsReplaceableGet(product: number): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'productos/productosreemplazables?productoId=' + product, {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const metaData: Array<MetaData> = [];

          success.items.forEach((value: any, index: number) => {
            metaData.push(new MetaData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', metaData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Set a product
   */
  productGet(product: number): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'productos/' + product, {}, headers).toPromise().then((success: any) => {
        const metaData: Array<MetaData> = [];

        success.planes.forEach((value: any, index: number) => {
          metaData.push(new MetaData().configData(value));
        });

        resolve(this.apiService.successDataPrepare('', metaData));
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  // Plans services

  /**
   * Get a list of plans
   */
  plansGet(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'planes?segmentoId=' + this.userService.currentUser.value.segment.id + '&sucursalId=' + this.userService.currentUser.value.subsidiary.id, {}, headers).toPromise().then((success: any) => {
        const responceData: Array<MetaData> = [];

        success.items.forEach((value: any, index: number) => {
          responceData.push(new MetaData().configData(value));
        });

        resolve(this.apiService.successDataPrepare('', responceData));
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.message, error));
      });
    });
  }

  /**
   * Get a plan
   */
  planGet(plan: number): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'planes/' + plan, {}, headers).toPromise().then((success: any) => {
        const metaData: Array<MetaData> = [];

        success.planes.forEach((value: any, index: number) => {
          metaData.push(new MetaData().configData(value));
        });

        resolve(this.apiService.successDataPrepare('', metaData));
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  // Reasons services

  /**
   * Get a list of reasons for unsubscription
   */
  reasonsGet(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'contratos/motivosbaja', {}, headers).toPromise().then((success: any) => {
        const responceData: Array<MetaData> = [];

        success.items.forEach((value: any, index: number) => {
          responceData.push(new MetaData().configData(value));
        });

        resolve(this.apiService.successDataPrepare('', responceData));
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.message, error));
      });
    });
  }
}
